import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  Search,
  RefreshCw,
  CheckCircle2,
  PackageCheck,
  AlertTriangle,
  CalendarClock,
  Hammer,
  Activity,
  ChevronRight,
} from "lucide-react";

const STATUSES = ["", "Available", "Checked Out", "In Calibration", "Under Repair", "Retired"];
const CATEGORIES = ["", "Test Equipment", "Hand Tool", "Power Tool", "Safety Gear", "Lifting Gear", "Other"];

const STATUS_PILL = {
  Available: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Checked Out": "bg-amber-100 text-amber-800 border-amber-300",
  "In Calibration": "bg-blue-100 text-blue-800 border-blue-300",
  "Under Repair": "bg-slate-200 text-slate-800 border-slate-400",
  Retired: "bg-rose-100 text-rose-800 border-rose-300",
  // Legacy fallbacks
  "In Yard": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "In Use": "bg-blue-100 text-blue-800 border-blue-300",
  Maintenance: "bg-slate-200 text-slate-800 border-slate-400",
  Damaged: "bg-rose-100 text-rose-800 border-rose-300",
};

const CAL_PILL = {
  overdue: "bg-rose-100 text-rose-800 border-rose-400",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  ok: "bg-emerald-100 text-emerald-700 border-emerald-300",
  none: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function Equipment() {
  const { isRunnerLevel } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [calibration, setCalibration] = useState("");
  const [tab, setTab] = useState("all"); // all | available | checked_out | overdue_cal

  const applyTab = (next) => {
    setTab(next);
    if (next === "all") {
      setStatus("");
      setCalibration("");
    } else if (next === "available") {
      setStatus("Available");
      setCalibration("");
    } else if (next === "checked_out") {
      setStatus("Checked Out");
      setCalibration("");
    } else if (next === "overdue_cal") {
      setStatus("");
      setCalibration("overdue");
    }
    setTimeout(load, 0);
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (category) params.category = category;
      if (status) params.status = status;
      if (calibration) params.calibration = calibration;
      const [s, i] = await Promise.all([
        api.get("/equipment/stats"),
        api.get("/equipment", { params }),
      ]);
      setStats(s.data);
      setItems(i.data);
    } catch {
      toast.error("Could not load equipment");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilter = () => load();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Yard Tools
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
            Equipment &amp; Tools
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Track every Meggar, IR tester, hand tool — checkout, return, calibration.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="equipment-refresh-btn"
            className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {isRunnerLevel && (
            <Link
              to="/equipment/new"
              data-testid="equipment-add-btn"
              className="rg-tap px-3 sm:px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-sm"
            >
              <Plus strokeWidth={3} className="h-4 w-4" />
              <span className="hidden sm:inline">Add Tool</span>
              <span className="sm:hidden">Add</span>
            </Link>
          )}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatTile
          label="Available"
          value={stats?.available ?? "—"}
          icon={CheckCircle2}
          theme="emerald"
          testid="stat-available"
          onClick={() => {
            setStatus("In Yard");
            setCalibration("");
            setTimeout(load, 0);
          }}
        />
        <StatTile
          label="Checked Out"
          value={(stats?.checked_out ?? 0) + (stats?.in_use ?? 0)}
          icon={PackageCheck}
          theme="amber"
          testid="stat-checked-out"
          onClick={() => {
            setStatus("Checked Out");
            setCalibration("");
            setTimeout(load, 0);
          }}
        />
        <StatTile
          label="Calibration Due Soon"
          value={stats?.calibration_due_soon ?? "—"}
          icon={CalendarClock}
          theme="amber"
          testid="stat-cal-due-soon"
          onClick={() => {
            setStatus("");
            setCalibration("due_soon");
            setTimeout(load, 0);
          }}
        />
        <StatTile
          label="Overdue"
          value={stats?.calibration_overdue ?? "—"}
          icon={AlertTriangle}
          theme="rose"
          testid="stat-cal-overdue"
          onClick={() => {
            setStatus("");
            setCalibration("overdue");
            setTimeout(load, 0);
          }}
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-3" role="tablist" data-testid="equipment-tabs">
        {[
          { key: "all", label: "All", count: stats?.total },
          { key: "available", label: "Available", count: stats?.available },
          { key: "checked_out", label: "Checked Out", count: (stats?.checked_out ?? 0) + (stats?.in_use ?? 0) },
          { key: "overdue_cal", label: "Overdue Calibration", count: stats?.calibration_overdue },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => applyTab(t.key)}
            data-testid={`equipment-tab-${t.key}`}
            className={`rg-tap px-3 sm:px-4 border-2 rounded-sm font-bold uppercase tracking-wider text-xs sm:text-sm flex items-center gap-1.5 ${
              tab === t.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
            }`}
          >
            {t.label}
            {typeof t.count !== "undefined" && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                  tab === t.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                }`}
              >
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="rg-card p-3 sm:p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onFilter()}
            data-testid="equipment-search"
            placeholder="Search Tool ID, Name, Serial…"
            className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white focus:border-slate-900 outline-none"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="equipment-filter-category"
          className="rg-tap border-2 border-slate-300 rounded-sm bg-white px-2 focus:border-slate-900 outline-none"
        >
          {CATEGORIES.map((c) => (
            <option key={c || "all"} value={c}>
              {c || "All categories"}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="equipment-filter-status"
          className="rg-tap border-2 border-slate-300 rounded-sm bg-white px-2 focus:border-slate-900 outline-none"
        >
          {STATUSES.map((s) => (
            <option key={s || "all"} value={s}>
              {s || "All statuses"}
            </option>
          ))}
        </select>
        <button
          onClick={onFilter}
          data-testid="equipment-apply-filter"
          className="rg-tap bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide text-sm rounded-sm"
        >
          Apply
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="equipment-empty">
          <Hammer className="h-10 w-10 mx-auto text-slate-400 mb-2" />
          <div className="font-display text-xl text-slate-900 uppercase mb-1">
            No tools match
          </div>
          <div className="text-sm text-slate-600">
            Add your first tool or adjust the filters above.
          </div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="equipment-list">
          {items.map((t) => (
            <li key={t.id}>
              <Link
                to={`/equipment/${t.id}`}
                data-testid={`equipment-row-${t.Tool_ID}`}
                className="block rg-card p-3 sm:p-4 hover:border-slate-900"
              >
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 grid place-items-center bg-slate-900 text-amber-400 rounded-sm flex-shrink-0">
                    <Wrench strokeWidth={2.5} className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-slate-900">
                        {t.Tool_ID}
                      </span>
                      <span className="font-bold text-slate-900 truncate">
                        {t.Tool_Name}
                      </span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
                          STATUS_PILL[t.Current_Status] || STATUS_PILL["In Yard"]
                        }`}
                      >
                        {t.Current_Status}
                      </span>
                      {t.calibration_state === "overdue" && (
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${CAL_PILL.overdue} flex items-center gap-1`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Cal Overdue
                        </span>
                      )}
                      {t.calibration_state === "due_soon" && (
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${CAL_PILL.due_soon}`}
                        >
                          Cal Due Soon
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-3">
                      {t.Category && (
                        <span className="inline-flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          {t.Category}
                        </span>
                      )}
                      {t.Serial_Number && (
                        <span>SN: <strong>{t.Serial_Number}</strong></span>
                      )}
                      {t.Location && <span>📍 {t.Location}</span>}
                      {t.Assigned_To && (
                        <span className="text-amber-700 font-bold">
                          → {t.Assigned_To}
                        </span>
                      )}
                      {t.Calibration_Due && (
                        <span>
                          Cal due <strong>{t.Calibration_Due}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0 self-center" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, theme, testid, onClick }) {
  const themes = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    rose: "bg-rose-50 border-rose-400 text-rose-900",
    slate: "bg-slate-50 border-slate-300 text-slate-900",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rg-card p-3 sm:p-4 text-left ${themes[theme] || themes.slate} hover:border-slate-900`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80">
          {label}
        </div>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="font-display text-2xl sm:text-3xl mt-1">{value}</div>
    </button>
  );
}
